"use client";

import { storage } from "@/lib/storage";
import { Info } from "lucide-react";
import * as React from "react";

interface GalleryInfoButtonProps {
	className?: string;
}

// Custom hook for storage with SSR safety and cross-tab sync
function useStorage(key: string, initialValue: boolean) {
	// State to store our value - start with true (hidden) while loading
	const [storedValue, setStoredValue] = React.useState<boolean>(true);
	const [isLoaded, setIsLoaded] = React.useState(false);

	// Load initial value from storage
	React.useEffect(() => {
		const loadValue = async () => {
			try {
				// Get from storage by key
				const item = await storage.getItem<boolean>(key);
				// Use stored value or initialValue if none exists
				const value = item !== null ? item : initialValue;
				setStoredValue(value);
			} catch (_) {
				// If error use initialValue
				setStoredValue(initialValue);
			} finally {
				setIsLoaded(true);
			}
		};

		loadValue();
	}, [key, initialValue]);

	// Listen for storage changes (cross-tab sync)
	React.useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === key && e.newValue !== null) {
				try {
					const newValue = JSON.parse(e.newValue);
					setStoredValue(newValue);
				} catch (_) {
					// Handle parsing error
				}
			}
		};

		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [key]);

	// Custom event for same-page sync
	React.useEffect(() => {
		const handleCustomStorageChange = (e: CustomEvent) => {
			if (e.detail.key === key) {
				setStoredValue(e.detail.value);
			}
		};

		window.addEventListener("storageChange", handleCustomStorageChange as EventListener);
		return () => window.removeEventListener("storageChange", handleCustomStorageChange as EventListener);
	}, [key]);

	// Return a wrapped version of useState's setter function that persists the new value to storage
	const setValue = React.useCallback(
		async (value: boolean) => {
			try {
				setStoredValue(value);
				// Save to storage
				await storage.setItem(key, value);

				// Dispatch custom event for same-page sync
				window.dispatchEvent(
					new CustomEvent("storageChange", {
						detail: { key, value },
					}),
				);
			} catch (_) {
				// Handle storage errors
			}
		},
		[key],
	);

	return [storedValue, setValue, isLoaded] as const;
}

export function GalleryInfoButton({ className }: GalleryInfoButtonProps) {
	const [showInfoPopup, setShowInfoPopup] = React.useState(false);
	const [infoAcknowledged, setInfoAcknowledged, isLoaded] = useStorage("gallery-info-acknowledged", false);

	// Don't render until we've loaded the localStorage value
	if (!isLoaded) {
		return null;
	}

	const toggleInfoPopup = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		e.nativeEvent.stopImmediatePropagation();
		setShowInfoPopup(s => !s);
	};

	return (
		<>
			{/* Info Button */}
			<div className={`absolute top-3 right-3 z-20 flex items-center h-8 ${className}`}>
				<div
					role="button"
					tabIndex={0}
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onClick={toggleInfoPopup}
					onKeyDown={e => {
						if (e.key === "Enter" || e.key === " ") {
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							toggleInfoPopup(e as any);
						}
					}}
					className={`backdrop-blur-sm text-white p-1 rounded-full w-8 h-8 flex items-center justify-center transition-all duration-300 cursor-pointer outline-none ${
						infoAcknowledged ? "bg-black/20 hover:bg-black/30 opacity-50" : "bg-black/50 hover:bg-black/70"
					}`}
					aria-label="Show gallery controls info"
				>
					<Info className="w-4 h-4" />
				</div>
			</div>

			{/* Info Popup */}
			{showInfoPopup && (
				<div className="absolute top-12 right-3 z-20 bg-black/80 backdrop-blur-sm text-white text-sm rounded-lg p-3 max-w-[80%]">
					<ul className="space-y-1 list-disc list-inside mb-3">
						<li>Navigation: Tap on left/right sides, swipe or use arrow keys</li>
						<li>Full screen: Double tap to toggle</li>
						<li>Back to exit full screen</li>
					</ul>
					<div
						role="button"
						tabIndex={0}
						onMouseDown={e => {
							e.preventDefault();
							e.stopPropagation();
						}}
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							e.nativeEvent.stopImmediatePropagation();
							setInfoAcknowledged(true);
							setShowInfoPopup(false);
						}}
						onKeyDown={e => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								e.stopPropagation();
								setInfoAcknowledged(true);
								setShowInfoPopup(false);
							}
						}}
						className="w-full bg-white/20 hover:bg-white/30 text-white text-xs py-2 px-3 rounded transition-colors cursor-pointer text-center outline-none"
					>
						Ok understood! Hide this
					</div>
				</div>
			)}
		</>
	);
}
